import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight, Check } from 'lucide-react';
import { useReducedMotion } from './motion';

/**
 * A closer look at one screen, without leaving the page.
 *
 * The screenshots under "the actual interface" used to navigate straight into
 * the application. That is a large step to take on the strength of a 190px
 * thumbnail: the reader has not yet been told what they are looking at, and the
 * page they land on assumes they have. This shows the screen at a size worth
 * looking at, says what is in it, and then offers the live page as a choice
 * rather than making it.
 */

export interface ShowcaseEntry {
  id: string;
  tab: string;
  src: string;
  alt: string;
  title: string;
  body: string;
  /** What the reader is looking at, in the order they will look at it. */
  points: { label: string; detail: string }[];
  /** What pressing through to the live page will let them do. */
  action: string;
}

export const SHOWCASE: ShowcaseEntry[] = [
  {
    id: 'dashboard',
    tab: 'dashboard',
    src: 'window',
    alt: 'The AgenticScale dashboard: agent counts, safety interventions and open incidents',
    title: 'The fleet dashboard',
    body: 'Every agent you run, what it is allowed to spend, and what the gate has done about it. This is the first screen after the download, with the sample fleet already loaded.',
    points: [
      { label: 'Every agent has an owner', detail: 'A team, not a service account — so a held action has somebody to go to.' },
      { label: 'A status, not a score', detail: 'Protected, monitoring or quarantined. A quarantined agent cannot act at all, whatever its profile says.' },
      { label: 'Interventions are counted', detail: 'How many actions were held for a person and how many were refused outright, over the whole fleet.' }
    ],
    action: 'Open the dashboard with the sample fleet'
  },
  {
    id: 'decision',
    tab: 'simulate',
    src: 'decision-block',
    alt: 'A decision panel showing BLOCK, with the reasons that produced it',
    title: 'A decision, with its reasons',
    body: 'Send an action through the gateway and watch it be allowed, held for a person, or refused — with every rule that contributed named. No model sits in this path, so the same input always reaches the same decision.',
    points: [
      { label: 'One of three outcomes', detail: 'It runs, it waits for a person, or it never runs. The two that did not happen stay drawn, so what was avoided is visible too.' },
      { label: 'Every reason is named', detail: 'Which boundary, which guardrail, and which framework control — OWASP, NIST or CWE — matched.' },
      { label: 'It can be argued with', detail: 'A decision you can replay is one you can appeal. Nothing here depends on a model having been in a particular mood.' }
    ],
    action: 'Send an action through the live gateway'
  },
  {
    id: 'evidence',
    tab: 'observability',
    src: 'traces',
    alt: 'Execution traces with an evidence timeline',
    title: 'The evidence trail',
    body: 'Every decision recorded with a SHA-256 of exactly what was decided, in a SQLite file on your own disk. Open it with any SQLite tool — there is no export to request and nobody to ask.',
    points: [
      { label: 'The whole run, in order', detail: 'Each action an agent attempted, what the gate returned, and the payload hash that proves what was weighed.' },
      { label: 'Hashed, not summarised', detail: 'The hash covers the decision input, so a record that was edited afterwards no longer matches it.' },
      { label: 'Yours, on your disk', detail: 'The hub writes to ~/.agenticscale/hub.db. Nothing is sent anywhere; the test suite fails on any socket that is not loopback.' }
    ],
    action: 'Open the traces and evidence timeline'
  }
];


/**
 * A scrolling region whose bottom edge says whether there is more below it.
 *
 * A cut that lands mid-sentence reads as a rendering fault rather than as a
 * scroll — the same thing that made the story panels and the feature thumbnails
 * look broken. The gradient appears only while there is something left to
 * scroll to, so a region that fits shows a clean edge.
 *
 * The scroller stays IN FLOW, and shrinks by way of `min-h-0` rather than being
 * positioned or sized. Both other ways of writing this are broken, and I shipped
 * each of them in turn:
 *
 *   `h-full` on a flex child resolves against nothing, so the region never
 *   clipped and its text sat over the footer, swallowing the clicks meant for
 *   the button underneath.
 *
 *   `absolute inset-0` clipped correctly and destroyed the dialog: out of flow,
 *   the wrapper has no content to take its height from, and `flex-1` is
 *   `flex: 1 1 0%` — a zero base with no free space to grow into, because the
 *   panel's height is capped by `max-h` and never set. The panel collapsed to
 *   its header and footer, 160px tall, while the screenshot inside it rendered
 *   at 408px entirely outside the box and was clipped away. Nothing between the
 *   title and the buttons was ever visible to anybody.
 *
 * `min-h-0` was the answer to the original overlap all along: it is what lets a
 * flex item shrink below its content and so what makes `overflow-y-auto` engage.
 */
const ScrollArea: React.FC<{
  children: React.ReactNode;
  className: string;
  frame: string;
  fade: string;
}> = ({ children, className, frame, fade }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [more, setMore] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const sync = () => setMore(el.scrollHeight - el.clientHeight - el.scrollTop > 8);
    sync();
    el.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    // The height changes when the screenshot arrives, which is after this runs.
    const images = [...el.querySelectorAll('img')];
    images.forEach((img) => img.addEventListener('load', sync));
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      images.forEach((img) => img.removeEventListener('load', sync));
      observer?.disconnect();
    };
  }, []);

  return (
    <div className={`relative ${frame}`}>
      <div ref={ref} className={className}>{children}</div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-10 transition-opacity duration-200"
        style={{ opacity: more ? 1 : 0, background: `linear-gradient(to top, ${fade}, transparent)` }}
      />
    </div>
  );
};

export const ShowcaseDialog: React.FC<{
  entry: ShowcaseEntry | null;
  dark: boolean;
  onClose: () => void;
  onOpenTab: (tab: string) => void;
}> = ({ entry, dark, onClose, onOpenTab }) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const returnTo = useRef<Element | null>(null);
  const reduced = useReducedMotion();

  const close = useCallback(() => {
    onClose();
    // Send focus back where it came from, or the reader loses their place.
    if (returnTo.current instanceof HTMLElement) returnTo.current.focus();
  }, [onClose]);

  useEffect(() => {
    if (!entry) return;
    returnTo.current = document.activeElement;
    closeRef.current?.focus();

    // The page behind must not scroll under the dialog.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { close(); return; }
      if (event.key !== 'Tab') return;
      // Keep focus inside: a dialog you can tab out of is a dialog a keyboard
      // user cannot get back into.
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [entry, close]);

  if (!entry) return null;

  // Rendered into <body>, not where it is written.
  //
  // It used to render inside the page's own `space-y-10` stack, and Tailwind's
  // space utility gives every child after the first a `margin-top: 2.5rem`. A
  // fixed box with `top: 0; bottom: 0` honours its margins, so the modal was
  // laid out 40px down and 40px short — leaving the top of the viewport
  // uncovered. That strip is exactly where the sticky header sits, so the
  // navbar stayed fully lit and fully clickable behind an open modal, and no
  // z-index would have helped: the backdrop was never over it to begin with.
  //
  // A portal fixes the cause rather than that symptom. A modal covers the
  // viewport, so it should not be a child of anything that lays out a page —
  // and this also puts it out of reach of any ancestor that later grows a
  // transform, a filter, or a stacking context.
  const dialog = (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="showcase-title"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        className={`absolute inset-0 cursor-default bg-slate-950/60 backdrop-blur-sm ${reduced ? '' : 'animate-backdrop-in'}`}
      />
      <div
        ref={panelRef}
        // The keyframes come from an animation utility rather than an inline
        // style: Tailwind only emits @keyframes for keyframes an animation
        // utility references, so an inline `animation:` would name a rule that
        // was never generated and the dialog would simply appear.
        className={`relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl border border-slate-900/10 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900 sm:max-h-[88vh] sm:rounded-3xl ${reduced ? '' : 'animate-showcase-in'}`}
      >
        <div className="flex flex-none items-start justify-between gap-4 border-b border-slate-900/5 px-5 py-4 dark:border-white/10 sm:px-7">
          <div className="min-w-0">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-brand-600 dark:text-brand-300">
              A closer look
            </p>
            <h2 id="showcase-title" className="mt-1 text-xl font-black tracking-tight text-slate-900 dark:text-white sm:text-2xl">
              {entry.title}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={close}
            className="flex h-9 w-9 flex-none items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            aria-label="Close this closer look"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <ScrollArea
          frame="flex min-h-0 flex-col"
          className="min-h-0 overflow-y-auto px-5 py-5 sm:px-7"
          fade={dark ? '#0f172a' : '#ffffff'}
        >
          {/* The screen itself, large enough to be worth opening the dialog for.
              Its own scroll, because these components are far taller than they
              are wide and cropping one to fit would defeat the point. */}
          <ScrollArea
            frame="overflow-hidden rounded-2xl border border-slate-900/10 bg-slate-50 dark:border-white/10 dark:bg-slate-950"
            className="max-h-[46vh] overflow-y-auto"
            fade={dark ? '#020617' : '#f8fafc'}
          >
            <img
              src={`/media/${dark ? 'dark' : 'light'}/${entry.src}.webp`}
              alt={entry.alt}
              className="block w-full"
              decoding="async"
            />
          </ScrollArea>

          <p className="mt-5 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{entry.body}</p>

          <ul className="mt-4 space-y-2.5">
            {entry.points.map((point) => (
              <li key={point.label} className="flex gap-2.5">
                <Check className="mt-0.5 h-4 w-4 flex-none text-brand-600 dark:text-brand-400" />
                <span className="min-w-0">
                  <span className="text-sm font-bold text-slate-900 dark:text-white">{point.label}. </span>
                  <span className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{point.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </ScrollArea>

        <div className="flex flex-none flex-col-reverse gap-2 border-t border-slate-900/5 px-5 py-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between sm:px-7">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={close}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              Close
            </button>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Lives at{' '}
              <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.7rem] text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                /{entry.tab}
              </code>
            </p>
          </div>
          <button
            type="button"
            onClick={() => { onOpenTab(entry.tab); close(); }}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-brand-500"
          >
            {entry.action} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document === 'undefined' ? dialog : createPortal(dialog, document.body);
};
